/* Groups simulation runs by the calendar date they were generated, in the
 * viewer's local time zone: one group per day for today and the 6 days before
 * it, then one per Monday-to-Sunday week for the rest of this week and the 3
 * weeks before it, then one per month for the rest of this month and the 11
 * months before it, then one per year.
 *
 * Every tier is counted in whole local calendar days, never in elapsed
 * milliseconds, so the time of day never matters: nothing moves between one
 * local midnight and the next, and a DST day of 23 or 25 hours can't move a
 * run by a day.
 *
 * A group's key is a stable id built only from the run's own date and its
 * tier ("day-2026-10-13", "week-2026-10-05" for the week starting that Monday,
 * "month-2026-09", "year-2025"), never its label and never "now". The page
 * keeps collapsed groups by key, so a group the user closed stays closed as
 * days pass and runs join it. A run only ever moves to a coarser tier, whose
 * key its week-, month- or year-mates already share.
 *
 * `value` is the results blob's `created`, an ISO 8601 instant, read in the
 * viewer's local zone like the "Generated ..." subtitle. */

const DAY_MS = 24 * 60 * 60 * 1000;

const DAY_TIER_DAYS = 6; // today and the 6 days before it
const WEEK_TIER_WEEKS = 3; // this week and the 3 weeks before it
const MONTH_TIER_MONTHS = 11; // this month and the 11 months before it

const DAY_KEY = /^day-(\d{4})-(\d{2})-(\d{2})$/;
const WEEK_KEY = /^week-(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_KEY = /^month-(\d{4})-(\d{2})$/;
const YEAR_KEY = /^year-(\d{4})$/;

/* A key names a calendar date, not an instant, so labels format that date at
 * UTC midnight in UTC. Formatting a local-midnight Date would show the same
 * text, except in zones whose DST change skips midnight. */
const DAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

const MONTH_YEAR_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

const pad = (value) => String(value).padStart(2, "0");

/* A whole-day count for the local calendar date of an instant: the number of
 * days from 1970-01-01 to that date. Comparisons between these integers are
 * calendar-day differences in the viewer's zone. */
const localDayNumber = (ms) => {
  const d = new Date(ms);
  return Math.round(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY_MS,
  );
};

const calendarDate = (dayNumber) => {
  const d = new Date(dayNumber * DAY_MS);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    weekday: (d.getUTCDay() + 6) % 7, // Monday 0 ... Sunday 6
  };
};

const mondayOf = (dayNumber) => dayNumber - calendarDate(dayNumber).weekday;

const isoDate = (dayNumber) => {
  const { year, month, day } = calendarDate(dayNumber);
  return `${year}-${pad(month)}-${pad(day)}`;
};

const dayNumberOfKeyDate = (year, month, day) =>
  Math.round(Date.UTC(Number(year), Number(month) - 1, Number(day)) / DAY_MS);

/**
 * Epoch milliseconds of local midnight on the local date of `ms`.
 *
 * @param {number} ms - epoch milliseconds
 * @returns {number}
 */
export function startOfLocalDay(ms) {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * The group a run generated at `value` falls in when it is `now`.
 *
 * @param {string} value - ISO 8601 instant, e.g. "2026-05-11T21:00:00Z".
 * @param {number} now - epoch milliseconds; any instant of the viewer's
 *   today gives the same answer (the page passes the start of today).
 * @returns {string | null} a stable group key, or null for an unreadable
 *   value (the Table shows no header for a null key).
 */
export function runTimeBucketKey(value, now) {
  if (!value) return null;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return null;

  const today = localDayNumber(now);
  /* A host clock a little ahead of the viewer's can stamp a just-finished run
   * tomorrow; it still belongs in today's group. */
  const run = Math.min(localDayNumber(time), today);

  if (today - run <= DAY_TIER_DAYS) return `day-${isoDate(run)}`;

  if (run >= mondayOf(today) - WEEK_TIER_WEEKS * 7) {
    return `week-${isoDate(mondayOf(run))}`;
  }

  const t = calendarDate(today);
  const r = calendarDate(run);
  if (r.year * 12 + r.month >= t.year * 12 + t.month - MONTH_TIER_MONTHS) {
    return `month-${r.year}-${pad(r.month)}`;
  }

  return `year-${r.year}`;
}

/**
 * The header text for a key from runTimeBucketKey, when it is `now`.
 *
 * @param {string | null} key
 * @param {number} now - epoch milliseconds; only its local date matters.
 * @returns {string} "Today", "Yesterday", "Oct 13, 2026", "Week of Oct 5, 2026",
 *   "September 2026" or "2025"; "" for an unknown key.
 */
export function runTimeBucketLabel(key, now) {
  if (!key) return "";

  const day = DAY_KEY.exec(key);
  if (day) {
    const dayNumber = dayNumberOfKeyDate(day[1], day[2], day[3]);
    const daysAgo = localDayNumber(now) - dayNumber;
    if (daysAgo === 0) return "Today";
    if (daysAgo === 1) return "Yesterday";
    return DAY_FORMATTER.format(dayNumber * DAY_MS);
  }

  const week = WEEK_KEY.exec(key);
  if (week) {
    const monday = dayNumberOfKeyDate(week[1], week[2], week[3]);
    return `Week of ${DAY_FORMATTER.format(monday * DAY_MS)}`;
  }

  const month = MONTH_KEY.exec(key);
  if (month) {
    return MONTH_YEAR_FORMATTER.format(
      Date.UTC(Number(month[1]), Number(month[2]) - 1, 1),
    );
  }

  const year = YEAR_KEY.exec(key);
  if (year) return year[1];

  return "";
}
